import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, MapPin, Clock3, UsersRound, Sparkles, Loader2 } from "lucide-react";
import { ClientAuthDialog } from "../components/fyllr/ClientAuthDialog";
import { ComingSoonGrid } from "../components/fyllr/ComingSoon";
import { useClientAuth } from "../context/ClientAuth";
import { getClientNotifications, getOpenings, getPaymentsConfig, listAvailableSessions, markClientNotificationsRead } from "../lib/api";
import { NotificationBell } from "../components/fyllr/NotificationBell";
import BookingDialog from "../components/fyllr/BookingDialog";
import { formatAppointment } from "../lib/datetime";

const money = (amount) => `$${Number(amount || 0).toFixed(0)}`;

const FILTERS = [
  { id: "all", label: "Everything" },
  { id: "opening", label: "Appointments" },
  { id: "session", label: "Wellness sessions" },
];
const TIME_FILTERS = [
  { id: "all", label: "All upcoming" },
  { id: "today", label: "Today" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "week", label: "This week" },
];

function sessionDateLabel(dateStr) {
  if (!dateStr) return "";
  const date = new Date(`${dateStr}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((date - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return date.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}
function sessionTimeLabel(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour = ((h + 11) % 12) + 1;
  return `${hour}:${String(m).padStart(2, "0")}${suffix}`;
}

function dayOffset(dateStr) {
  if (!dateStr) return null;
  const date = new Date(`${dateStr}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((date - today) / 86400000);
}

function isoDayOffset(iso) {
  const local = new Date(new Date(iso).toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
  local.setHours(0, 0, 0, 0);
  const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
  today.setHours(0, 0, 0, 0);
  return Math.round((local - today) / 86400000);
}

function openingDayOffset(time) {
  const label = String(time || "").toLowerCase();
  if (/(^|\s)(tomorrow)(\s|$)/.test(label)) return 1;
  if (/(^|\s)(today|tonight)(\s|$)/.test(label)) return 0;
  if (/\b(mon|tue|wed|thu|fri|sat|sun)(day)?\b/.test(label)) {
    const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const matched = days.findIndex((day) => label.includes(day));
    return (matched - new Date().getDay() + 7) % 7;
  }
  return 0;
}

export default function Marketplace() {
  const { client, logout } = useClientAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openings, setOpenings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState(() => ["all", "today", "tomorrow", "week"].includes(searchParams.get("time")) ? searchParams.get("time") : "all");
  const [category, setCategory] = useState(() => searchParams.get("category") || "all");
  const [suburb, setSuburb] = useState(() => searchParams.get("suburb") || "all");

  const [authOpen, setAuthOpen] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pendingBook, setPendingBook] = useState(null);
  const welcome = searchParams.get("welcome") === "1";

  useEffect(() => { getPaymentsConfig().then((cfg) => setPaymentsEnabled(Boolean(cfg?.enabled))).catch(() => setPaymentsEnabled(false)); }, []);

  const onBook = (opening) => {
    if (client) { setSelected(opening); return; }
    setPendingBook(opening);
    setAuthOpen(true);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [ops, sess] = await Promise.all([getOpenings(), listAvailableSessions()]);
        if (!mounted) return;
        setOpenings(Array.isArray(ops) ? ops : []);
        setSessions(Array.isArray(sess) ? sess : []);
      } catch (error) {
        console.warn("Marketplace inventory load failed; showing the safe empty state.", error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const items = useMemo(() => {
    const openingItems = openings.map((o) => ({ type: "opening", id: `o-${o.id}`, category: o.category, data: o }));
    const sessionItems = sessions.map((s) => ({ type: "session", id: `s-${s.id}`, category: s.category, data: s }));
    let all = [...openingItems, ...sessionItems];
    all = all.filter((item) => {
      const offset = item.type === "session" ? dayOffset(item.data.date_str) : item.data.appointment_at ? isoDayOffset(item.data.appointment_at) : openingDayOffset(item.data.time);
      if (timeFilter === "all") return offset === null || offset >= 0;
      if (timeFilter === "today") return offset === 0;
      if (timeFilter === "tomorrow") return offset === 1;
      return offset !== null && offset >= 0 && offset < 7;
    });
    if (filter !== "all") all = all.filter((i) => i.type === filter);
    if (category !== "all") all = all.filter((i) => i.category === category);
    if (suburb !== "all") all = all.filter((i) => i.data.suburb === suburb);
    return all.sort((a, b) => String(b.data.created_at || "").localeCompare(String(a.data.created_at || "")));
  }, [openings, sessions, filter, timeFilter, category, suburb]);

  const categories = useMemo(() => {
    const set = new Set([...openings, ...sessions].map((i) => i.category).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [openings, sessions]);

  const suburbs = useMemo(() => {
    const values = new Set([...openings, ...sessions].map((item) => item.suburb).filter(Boolean));
    return ["all", ...Array.from(values).sort()];
  }, [openings, sessions]);

  return (
    <div className="min-h-screen bg-bone pt-14 md:pt-16" data-testid="marketplace-page">
      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <p className="text-[10px] tracking-[0.3em] uppercase text-mocha" data-testid="marketplace-eyebrow">FYLLR early access</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[0.95]" data-testid="marketplace-heading">Melbourne, we’re coming.</h1>
            <p className="mt-5 text-base text-ink/60 max-w-lg" data-testid="marketplace-sub">Preview founding studios and their opening styles now. Public booking requests open 7 February 2027.</p>
          </div>
          <div className="flex items-center gap-3" data-testid="marketplace-account">
            {client ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-ink/70" data-testid="marketplace-client-name">Hi, {client.name?.split(" ")[0] || "there"}</span>
                <NotificationBell fetchFeed={getClientNotifications} markRead={markClientNotificationsRead} testId="client-notifications" />
                <Link to="/my-bookings" className="text-[13px] text-burgundy hover:text-espresso underline underline-offset-2" data-testid="marketplace-my-bookings">My bookings</Link>
                <button type="button" onClick={logout} className="text-[13px] text-ink/55 hover:text-ink underline underline-offset-2" data-testid="marketplace-logout">Sign out</button>
              </div>
            ) : (
              <button type="button" onClick={() => setAuthOpen(true)} className="rounded-full border border-sand px-5 py-2.5 text-sm text-ink hover:border-espresso transition-colors" data-testid="marketplace-signin">Sign in</button>
            )}
          </div>
        </div>
        {welcome && <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border border-burgundy/30 bg-burgundy/8 px-5 py-4" data-testid="marketplace-welcome-banner"><div><p className="font-serif text-2xl text-espresso">You’re in.</p><p className="mt-1 text-sm text-ink/65">Your FYLLR account is ready. Browse an opening whenever you are.</p></div><button type="button" onClick={() => setSearchParams({})} className="text-[10px] uppercase tracking-[0.18em] text-espresso underline underline-offset-4" data-testid="marketplace-welcome-dismiss">Dismiss</button></div>}
      </section>

      {/* Filters */}
      <section className="relative md:sticky md:top-16 z-30 bg-bone/95 backdrop-blur-md border-y border-sand/50">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-4 flex flex-col gap-3">
          <div className="flex gap-2" role="tablist" data-testid="marketplace-type-filters">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" onClick={() => setFilter(f.id)} className={`rounded-full px-4 py-2 text-sm transition-colors ${filter === f.id ? "bg-espresso text-bone" : "border border-sand text-ink/65 hover:border-espresso"}`} data-testid={`marketplace-filter-${f.id}`}>{f.label}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" role="tablist" data-testid="marketplace-time-filters">
            {TIME_FILTERS.map((item) => <button key={item.id} type="button" onClick={() => setTimeFilter(item.id)} className={`rounded-full px-4 py-2 text-sm transition-colors ${timeFilter === item.id ? "bg-burgundy text-bone" : "border border-sand text-ink/65 hover:border-espresso"}`} data-testid={`marketplace-time-${item.id}`}>{item.label}</button>)}
          </div>
          <label className="sr-only" htmlFor="marketplace-suburb" data-testid="marketplace-suburb-label">Filter by suburb</label>
          <select id="marketplace-suburb" value={suburb} onChange={(event) => setSuburb(event.target.value)} className="w-full max-w-xs border border-sand bg-bone px-3 py-2 text-sm text-ink outline-none focus:border-espresso" data-testid="marketplace-suburb-filter">
            {suburbs.map((item) => <option key={item} value={item}>{item === "all" ? "All Melbourne suburbs" : item}</option>)}
          </select>
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="marketplace-category-filters">
            {categories.map((c) => (
              <button key={c} type="button" onClick={() => setCategory(c)} className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${category === c ? "bg-ink/90 text-bone" : "border border-sand/70 text-ink/55 hover:text-ink"}`} data-testid={`marketplace-cat-${c}`}>{c === "all" ? "All services" : c}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-10 md:py-14">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink/50" data-testid="marketplace-loading"><Loader2 className="animate-spin mr-2" size={18} /> Loading the marketplace…</div>
        ) : openings.length === 0 && sessions.length === 0 ? (
          <ComingSoonGrid />
        ) : items.length === 0 ? (
          <div className="py-24 text-center" data-testid="marketplace-empty"><p className="font-serif text-2xl text-ink">Nothing matches that filter right now.</p><p className="mt-2 text-sm text-ink/55">Try another time, suburb or service category.</p></div>
        ) : (
          <>
            <p className="mb-6 text-sm text-ink/50" data-testid="marketplace-count">{items.length} {items.length === 1 ? "deal" : "deals"} · newest first</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {items.map((item) => item.type === "opening"
                ? <OpeningCard key={item.id} opening={item.data} onBook={paymentsEnabled ? onBook : null} />
                : <SessionMarketCard key={item.id} session={item.data} />)}
            </div>
          </>
        )}
      </section>

      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pb-16">
        <Link to="/join" className="text-sm text-ink/50 hover:text-ink underline underline-offset-2" data-testid="marketplace-waitlist-link">Get early access before public bookings open →</Link>
      </div>

      <ClientAuthDialog
        open={authOpen}
        onClose={() => { setAuthOpen(false); setPendingBook(null); }}
        onSuccess={() => { setAuthOpen(false); if (pendingBook) { setSelected(pendingBook); setPendingBook(null); } }}
        reason={pendingBook ? "Sign in to book and pay for this opening." : "Sign in to your FYLLR account. Public bookings open 7 February 2027."}
      />
      <BookingDialog listing={selected} open={Boolean(selected)} onClose={() => setSelected(null)} prefill={client ? { name: client.name, email: client.email, phone: client.phone } : null} />
    </div>
  );
}

function OpeningCard({ opening, onBook }) {
  const discount = opening.original_price > opening.offer_price;
  return (
    <article className="group bg-bone border border-sand/80 overflow-hidden flex flex-col" data-testid={`market-opening-${opening.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden">
        {opening.image && <img src={opening.image} alt={opening.service} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
        <span className="absolute left-3 top-3 bg-bone/90 text-ink text-[10px] tracking-[0.15em] uppercase px-2.5 py-1" data-testid={`market-opening-type-${opening.id}`}>Appointment</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[10px] tracking-[0.2em] uppercase text-mocha" data-testid={`market-opening-salon-${opening.id}`}>{opening.salon}</p>
        <h3 className="mt-2 font-serif text-2xl text-ink leading-tight" data-testid={`market-opening-name-${opening.id}`}>{opening.service}</h3>
        <div className="mt-3 space-y-1.5 text-sm text-ink/60">
          <p className="flex items-center gap-2" data-testid={`market-opening-when-${opening.id}`}><Clock3 size={14} className="text-mocha" />{opening.appointment_at ? formatAppointment(opening.appointment_at) : opening.time}</p>
          <p className="flex items-center gap-2"><MapPin size={14} className="text-mocha" />{opening.suburb} · {opening.category}</p>
        </div>
        <div className="mt-5 pt-4 border-t border-sand/60 flex items-center justify-between gap-3 mt-auto">
          <p className="font-serif text-2xl text-espresso" data-testid={`market-opening-price-${opening.id}`}>{money(opening.offer_price)}{discount && <span className="ml-2 font-sans text-xs text-ink/40 line-through" data-testid={`market-opening-original-price-${opening.id}`}>{money(opening.original_price)}</span>}</p>
          {onBook
            ? <button type="button" onClick={() => onBook(opening)} className="inline-flex items-center gap-2 bg-espresso text-bone rounded-full px-5 py-2.5 text-sm hover:bg-ink transition-colors" data-testid={`market-book-${opening.id}`}>Book <ArrowRight size={14} /></button>
            : <Link to="/join" className="inline-flex items-center gap-2 bg-espresso text-bone rounded-full px-4 py-2.5 text-sm hover:bg-ink transition-colors" data-testid={`market-early-access-${opening.id}`}>Get early access <ArrowRight size={14} /></Link>}
        </div>
      </div>
    </article>
  );
}

function SessionMarketCard({ session }) {
  const low = session.spots_left <= 2;
  return (
    <article className="group bg-ink text-bone overflow-hidden flex flex-col" data-testid={`market-session-${session.id}`}>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase text-bone/70"><Sparkles size={12} /> Session</span>
          <span className={`text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 border ${low ? "border-sand text-sand" : "border-bone/30 text-bone/60"}`} data-testid={`market-session-spots-${session.id}`}>{session.spots_left} left</span>
        </div>
        <p className="mt-4 text-[10px] tracking-[0.2em] uppercase text-bone/50">{session.category}</p>
        <h3 className="mt-1.5 font-serif text-2xl leading-tight" data-testid={`market-session-name-${session.id}`}>{session.service_name}</h3>
        <div className="mt-3 space-y-1.5 text-sm text-bone/65">
          <p className="flex items-center gap-2"><Clock3 size={14} className="text-sand" />{sessionDateLabel(session.date_str)} · {sessionTimeLabel(session.time)}</p>
          <p className="flex items-center gap-2"><UsersRound size={14} className="text-sand" />{session.duration_min} min · {session.salon_name}</p>
        </div>
        <div className="mt-5 pt-4 border-t border-bone/15 flex items-center justify-between gap-3 mt-auto">
          <p className="font-serif text-2xl text-bone" data-testid={`market-session-price-${session.id}`}>{money(session.price)} <span className="font-sans text-xs text-bone/50">/ spot</span></p>
          <Link to="/join" className="inline-flex items-center gap-2 bg-bone text-ink rounded-full px-4 py-2.5 text-sm hover:bg-sand transition-colors" data-testid={`market-early-access-${session.id}`}>Get early access <ArrowRight size={14} /></Link>
        </div>
      </div>
    </article>
  );
}
